import { useState, useEffect, useCallback } from "react";
import { ActivityChangeType } from "@/components/ActivityStream/ActivityStreamItem";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSaveFile } from "@/hooks/useSaveFile";
import { useRunTerminalCommand, useStartServer } from "@/utils/devServerUtils";
import { showConfirmationModal } from "@/store/zustand/DependencyConfirmationModal";
import { ReactComponentMetadata } from "@/utils/types";
import { useSelector } from "react-redux";
import { useFilePathToFileTreeLookup } from "../components/Canvas/hooks/useFileTreeLookup";
import {
  getUnexpiredToken,
  useActiveProject,
  useCanvasId,
} from "@/utils/utils";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useSession } from "@clerk/clerk-react";
import { track } from "@/utils/events/tracker";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

type ComponentFileType = {
  file_name: string;
  file_contents: string;
  uses_typescript: boolean;
  is_shadcn_component: boolean;
  id?: number;
  component_id?: number;
};

/**
 * Custom hook to handle React components from drag and drop or copy/paste operations
 */
export const useReactComponentHandler = () => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const startServer = useStartServer();
  const runTerminalCommand = useRunTerminalCommand();
  const saveFile = useSaveFile();
  const [isProcessing, setIsProcessing] = useState(false);
  const project = useActiveProject();
  const canvasId = useCanvasId();

  const { isLoaded, session } = useSession();

  const basePathToProject = useSelector(
    (state: any) => state.codebase.basePathToProject
  );

  const dependencies = useCodebaseStore((state) => state.dependencies);
  const updateDependencies = useCodebaseStore(
    (state) => state.updateDependencies
  );

  const filePathToFileTreeLookup = useFilePathToFileTreeLookup();

  /**
   * Gathers new shadcn files and missing dependencies.
   * @param componentFiles Array of component files from metadata
   * @param requiredDependencies Array of required npm dependencies from metadata
   * @returns Object containing lists of items to confirm and non-shadcn files
   */
  const prepareConfirmationItems = useCallback(
    (
      componentFiles: ComponentFileType[],
      requiredDependencies: ReactComponentMetadata[]
    ) => {
      const newShadcnFiles: ComponentFileType[] = [];
      const nonShadcnFiles: { [key: string]: string } = {};
      const missingDependencies: string[] = [];

      if (componentFiles && basePathToProject) {
        for (const file of componentFiles) {
          if (file.is_shadcn_component) {
            const filePath =
              `${basePathToProject}/src/${file.file_name}`.replace(
                /\/\/+/g,
                "/"
              );
            if (!filePathToFileTreeLookup[filePath]) {
              newShadcnFiles.push(file);
            }
          } else {
            nonShadcnFiles[file.file_name] = file.file_contents;
          }
        }
      }

      if (requiredDependencies && requiredDependencies.length > 0) {
        const requiredDependencyNames = requiredDependencies.map(
          (dep) => dep.import_name
        );
        requiredDependencyNames.forEach((depName) => {
          if (!dependencies.regularDependencies[depName]) {
            missingDependencies.push(depName);
          }
        });
      }

      return { newShadcnFiles, missingDependencies, nonShadcnFiles };
    },
    [basePathToProject, filePathToFileTreeLookup, dependencies]
  );

  /**
   * Handles the confirmation process and subsequent actions (saving files, installing deps).
   * @param newShadcnFiles Array of new shadcn component file objects
   * @param missingDependencies Array of missing dependency names
   * @returns boolean indicating if the operation should proceed (user confirmed)
   */
  const confirmAndAct = async (
    newShadcnFiles: ComponentFileType[],
    missingDependencies: string[]
  ): Promise<boolean> => {
    const fileNames = newShadcnFiles.map((f) => f.file_name);

    if (fileNames.length === 0 && missingDependencies.length === 0) {
      return true;
    }

    const installAction = async () => {
      if (newShadcnFiles.length > 0 && basePathToProject) {
        const fileToContents: { [key: string]: string } = {};
        newShadcnFiles.forEach((file) => {
          const filePath = `${basePathToProject}/src/${file.file_name}`.replace(
            /\/\/+/g,
            "/"
          );
          fileToContents[filePath] = file.file_contents;
        });

        await saveFile({
          fileToContentsToSave: fileToContents,
          saveActivityType: ActivityChangeType.ADD_COMPONENT,
          onlyCreate: true,
        });
      }

      if (missingDependencies.length > 0) {
        const installCommand = `${
          project?.packageManager
        } install ${missingDependencies.join(" ")}`;
        const result = await runTerminalCommand({
          command: installCommand,
        });

        if (result.success) {
          const newDependencies = missingDependencies.reduce(
            (acc, dep) => ({
              ...acc,
              [dep]: dependencies.regularDependencies[dep] || "",
            }),
            {}
          );
          updateDependencies(newDependencies);
          startServer({});
        }
      }
    };

    return await showConfirmationModal(
      fileNames,
      missingDependencies,
      installAction
    );
  };

  /**
   * Fetch component data from Supabase by ID, check requirements, confirm, and act.
   * @param componentId Component ID
   * @returns Object containing component data and files if successful
   */
  const loadComponent = async (componentId: string) => {
    return await getUnexpiredToken(session).then(async (supabaseJWT) => {
      if (!supabaseClient || !canvasId || !supabaseJWT || !basePathToProject) {
        console.error(
          "Missing Supabase client, project context, JWT, or base project path."
        );
        if (!basePathToProject)
          console.error("basePathToProject from Redux is null/undefined.");
        return { success: false };
      }

      setIsProcessing(true);
      try {
        const { data, error } = await supabaseClient
          .schema("reactcomponents")
          .from("components")
          .select(
            "*, authors(first_name, last_name), component_files(file_name, file_contents, uses_typescript, is_shadcn_component), npm_dependencies"
          )
          .eq("component_files.is_shadcn_component", false)
          .eq("id", Number(componentId))
          .single();

        if (error) {
          console.error("Error fetching component:", error);
          return { success: false };
        }

        const componentFiles = data.component_files as ComponentFileType[];
        const requiredDependencies =
          data.npm_dependencies as unknown as ReactComponentMetadata[];

        const { newShadcnFiles, missingDependencies, nonShadcnFiles } =
          prepareConfirmationItems(componentFiles, requiredDependencies);

        const contents = data.contents;
        const extraPathToContents = componentFiles.reduce(
          (acc: Record<string, string>, file) => {
            acc[file.file_name] = file.file_contents;
            return acc;
          },
          {}
        );

        return {
          success: true,
          contents,
          extraPathToContents,
          componentData: data,
          nonShadcnFiles,
        };
      } catch (error) {
        console.error("Error processing component:", error);
        track("REACT_COMPONENT_ERROR", {
          componentId,
          type: "LOAD",
          error: error?.toString(),
        });
        return { success: false };
      } finally {
        setIsProcessing(false);
      }
    });
  };

  /**
   * Process component metadata (e.g., from drop), check requirements, confirm, and act.
   * @param metadata Component metadata including files and dependencies
   * @returns Object indicating success and potentially non-shadcn files (though action is handled internally)
   */
  const installDepsForComponent = async (metadata: {
    component_files: ComponentFileType[];
    npm_dependencies: ReactComponentMetadata[];
  }): Promise<{
    success: boolean;
    nonShadcnFiles?: { [key: string]: string };
  }> => {
    if (!session) {
      return { success: false };
    }

    return getUnexpiredToken(session).then(async (supabaseJWT) => {
      if (!canvasId || !basePathToProject) {
        console.error(
          "Missing project context, JWT, or base project path for dependency check."
        );
        return { success: false };
      }

      setIsProcessing(true);
      try {
        const { newShadcnFiles, missingDependencies, nonShadcnFiles } =
          prepareConfirmationItems(
            metadata.component_files,
            metadata.npm_dependencies
          );

        const success = await confirmAndAct(
          newShadcnFiles,
          missingDependencies
        );

        return { success, nonShadcnFiles };
      } catch (error) {
        console.error("Error processing component metadata:", error);
        track("REACT_COMPONENT_ERROR", {
          npm_dependencies: metadata.npm_dependencies,
          type: "INSTALL",
          error: error?.toString(),
        });

        return { success: false };
      } finally {
        setIsProcessing(false);
      }
    });
  };
  return {
    isProcessing,
    loadComponent,
    installDepsForComponent,
  };
};
